/**
 * pickLatestOrder — decide which of the per-vertical order slices a shared
 * confirmation screen should actually show.
 *
 * Every confirmation screen reads all of them (general, brand, restaurant,
 * retail/wholesale, healthcare) because any checkout can land here, and each
 * screen used to choose with a fixed `||` chain that put brandOrder first.
 * The slices are not cleared between checkouts, so a customer who had placed
 * a brand order earlier in the session and then ordered from a restaurant was
 * shown the BRAND order on the restaurant confirmation screen: wrong order id,
 * wrong items, wrong total — and "Continue shopping" went on to clear the cart
 * belonging to that stale order rather than the one just emptied.
 *
 * Recency decides instead. Whichever order was created last is the one the
 * customer just placed, whatever vertical it came from. That also survives the
 * card and Airtel rails, which come back from Flutterwave into a fresh page
 * load where no navigation state is left to consult.
 *
 * Orders are stamped by Mongoose ({ timestamps: true }) and the created
 * document is returned whole, so createdAt is reliably present. When nothing
 * carries a usable date the first candidate wins, which is exactly the old
 * precedence — pass the candidates in the old order and the fallback is a
 * no-op.
 *
 * @param {Array<object|null|undefined>} candidates in legacy precedence order
 * @returns {object|null}
 */
const stampOf = (order) => {
  const raw = order?.createdAt || order?.updatedAt || order?.orderDate;
  const parsed = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

export const pickLatestOrder = (candidates = []) => {
  const real = candidates.filter(
    (order) => order && typeof order === "object" && order._id
  );
  if (!real.length) return null;

  const dated = real
    .map((order) => ({ order, at: stampOf(order) }))
    .filter((entry) => entry.at != null);
  if (!dated.length) return real[0];

  // Strictly greater, so an exact tie keeps the earlier-declared candidate and
  // the legacy precedence still decides.
  return dated.reduce((best, entry) => (entry.at > best.at ? entry : best)).order;
};

export default pickLatestOrder;
